import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { Chant } from "../../chants/Chant";
import { DEFAULT_TONES } from "../../chants/default-tones";
import {
    OutstandingEnding,
    getOutstandingEndingsForPsalm,
    getPointedText,
    getPsalmRefFromText,
} from "../../chants/utils";
import { BACKGROUND_COLOR, RUBRIC_COLOR, TEXT_FONT } from "../../tokens";
import { useLocalStorage } from "../../utils/local-storage";

type Part =
    | { type: "space" }
    | { type: "hyphen" }
    | { type: "syllable"; text: string; stressed: boolean };

const getParts = (text: string) => {
    const parts: Part[] = [];

    Array.from(text).forEach((char) => {
        if (char === "_") {
            parts.push({ type: "hyphen" });
        } else if (char === " ") {
            parts.push({ type: "space" });
        } else if (char === "/") {
            parts.push({ type: "syllable", text: "", stressed: true });
        } else {
            if (parts.length > 0) {
                const lastPart = parts[parts.length - 1];
                if (lastPart.type === "syllable") {
                    lastPart.text += char;
                } else {
                    parts.push({
                        type: "syllable",
                        text: char,
                        stressed: false,
                    });
                }
            } else {
                parts.push({ type: "syllable", text: char, stressed: false });
            }
        }
    });

    return parts;
};

export const TagPage: React.FC<{}> = () => {
    const { psalm } = useParams<{ psalm: string }>();

    const [emphasisDb, setEmphasisDb] = useLocalStorage(
        "emphasis-db",
        undefined
    );

    const [parts, setParts] = useState<Part[]>([]);
    const [remaining, setRemaining] = useState(0);
    const [ending, setEnding] = useState<OutstandingEnding | undefined>(
        undefined
    );

    const updatedText = parts
        .map((part) => {
            switch (part.type) {
                case "space":
                    return " ";
                case "hyphen":
                    return "_";
                case "syllable":
                    return (part.stressed ? "/" : "") + part.text;
            }
        })
        .join("");

    const save = useCallback(() => {
        if (!ending) {
            return;
        }

        const newEmphasisDb = {
            ...emphasisDb,
            [ending.ending]: {
                tagged: updatedText,
                examples: [],
            },
        };

        setEmphasisDb(newEmphasisDb);

        load(newEmphasisDb);
    }, [ending, emphasisDb, setEmphasisDb, updatedText]);

    const [disabled, setDisabled] = useState(false);

    const load = useCallback(
        (eDb?: any) => {
            if (psalm === "load") {
                return;
            }

            const outstandingEndings = getOutstandingEndingsForPsalm(
                psalm,
                eDb ?? emphasisDb
            );

            if (outstandingEndings.length > 0) {
                const current = outstandingEndings[0];
                setParts(getParts(current.ending));
                setEnding(current);
                setRemaining(outstandingEndings.length);
                setDisabled(true);
                setTimeout(() => {
                    setDisabled(false);
                }, 2000);
            } else {
                setRemaining(0);
            }
        },
        [emphasisDb, psalm]
    );

    useEffect(() => {
        if (parts.length === 0) {
            load();
        }
    }, [parts, load]);

    const tones = DEFAULT_TONES[getPsalmRefFromText(psalm).number];

    const preview = useMemo(() => {
        try {
            if (ending) {
                return (
                    <div style={{ paddingTop: 16 }}>
                        {tones.map((t) => {
                            return (
                                <>
                                    <div>Actual tone ({t.tone}):</div>
                                    <Chant
                                        pointedText={getPointedText(
                                            [[updatedText, updatedText]],
                                            t.tone,
                                            false,
                                            false,
                                            {
                                                [ending.ending]: {
                                                    tagged: updatedText,
                                                    examples: [],
                                                },
                                            }
                                        )}
                                        dropCap={false}
                                        boldMode="all"
                                    />
                                </>
                            );
                        })}
                        <div>Sanity check for other tones:</div>
                        <Chant
                            pointedText={getPointedText(
                                [[updatedText, updatedText]],
                                "6b",
                                false,
                                false,
                                {
                                    [ending.ending]: {
                                        tagged: updatedText,
                                        examples: [],
                                    },
                                }
                            )}
                            dropCap={false}
                            boldMode="all"
                        />
                        <Chant
                            pointedText={getPointedText(
                                [[updatedText, updatedText]],
                                "2.1",
                                false,
                                false,
                                {
                                    [ending.ending]: {
                                        tagged: updatedText,
                                        examples: [],
                                    },
                                }
                            )}
                            dropCap={false}
                            boldMode="all"
                        />
                    </div>
                );
            }
        } catch (error) {
            return <div style={{ paddingTop: 16 }}>{error.message}</div>;
        }
    }, [ending, updatedText, tones]);

    if (remaining === 0) {
        return <div>Done!</div>;
    } else {
        return (
            <>
                <div style={{ textAlign: "center", fontWeight: "bold" }}>
                    {remaining} remaining
                </div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        fontSize: "200%",
                        padding: 32,
                        fontWeight: "bold",
                    }}
                >
                    {parts.map((part, partIndex) => {
                        switch (part.type) {
                            case "space":
                                return <div>&nbsp;</div>;
                            case "hyphen":
                                return <div>-</div>;
                            case "syllable":
                                return (
                                    <div
                                        style={
                                            part.stressed
                                                ? {
                                                      textDecoration:
                                                          "underline",
                                                      cursor: "pointer",
                                                  }
                                                : { cursor: "pointer" }
                                        }
                                        onClick={() => {
                                            setParts(
                                                parts.map((p, pi) => {
                                                    if (
                                                        pi === partIndex &&
                                                        p.type === "syllable"
                                                    ) {
                                                        return {
                                                            ...p,
                                                            stressed:
                                                                !p.stressed,
                                                        };
                                                    } else {
                                                        return p;
                                                    }
                                                })
                                            );
                                        }}
                                    >
                                        {part.text}
                                    </div>
                                );
                        }
                    })}
                </div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        paddingBottom: 32,
                    }}
                >
                    <button
                        style={{
                            backgroundColor: RUBRIC_COLOR,
                            color: BACKGROUND_COLOR,
                            border: "none",
                            borderRadius: 4,
                            padding: 12,
                            fontFamily: TEXT_FONT,
                            fontSize: "120%",
                            fontWeight: "bold",
                            opacity: disabled ? 0.5 : 1.0,
                            transition: "all 0.5s",
                        }}
                        disabled={disabled}
                        onClick={() => {
                            save();
                        }}
                    >
                        Continue
                    </button>
                </div>
                {preview}
            </>
        );
    }
};
